
export interface GlossaryTerm {
    term: string;
    definition: string;
    category: 'Geopolitical' | 'Security' | 'Technical' | 'Strategic';
    acronym?: string;
    externalUrl?: string;
}

export const GLOSSARY_DATA: GlossaryTerm[] = [
    {
        term: 'PMESII-PT',
        acronym: 'PMESII-PT',
        definition: 'A framework used by analysts to examine the operational environment through Political, Military, Economic, Social, Infrastructure, Information, Physical Environment, and Time variables.',
        category: 'Geopolitical',
        externalUrl: 'https://oe.t2com.army.mil/2024/04/29/scenario-development-phase-2-design/'
    },
    {
        term: 'ASCOPE',
        acronym: 'ASCOPE',
        definition: 'Civil considerations framework: Area, Structures, Capabilities, Organizations, People, and Events. Used to understand the human geography of an operational area.',
        category: 'Geopolitical',
        externalUrl: 'https://www.army.mil/article/200199/a_crisis_exists_an_easy_mnemonic_to_remember_the_sustainment_principles'
    },
    {
        term: 'Risk Appetite',
        definition: 'The level of risk that an organization is prepared to accept in pursuit of its objectives, before action is deemed necessary to reduce it.',
        category: 'Strategic'
    },
    {
        term: 'Consequence Rating',
        definition: 'Impact analysis on supply chains, personnel, brand equity, and business continuity. A core component of risk quantification.',
        category: 'Strategic',
        externalUrl: 'https://en.wikipedia.org/wiki/Business_impact_analysis'
    },
    {
        term: 'Composite Risk Index',
        acronym: 'CRI',
        definition: 'A proprietary numerical score (0-100) that normalizes qualitative risk factors and quantitative data feeds into a unified intensity metric.',
        category: 'Strategic'
    },
    {
        term: 'Threat Actor',
        definition: 'An individual or group that poses a threat to an organization\'s assets, including criminals, terrorists, state-sponsored entities, or fixated persons.',
        category: 'Security'
    },
    {
        term: 'Open-Source Intelligence',
        acronym: 'OSINT',
        definition: 'Intelligence collected from publicly available sources, including social media, news reports, and public records.',
        category: 'Strategic',
        externalUrl: 'https://en.wikipedia.org/wiki/Open-source_intelligence'
    },
    {
        term: 'FSAT',
        acronym: 'FSAT',
        definition: 'Facility Security Assessment Tool. A proprietary digital audit process used to evaluate physical security controls and operational vulnerabilities.',
        category: 'Security'
    },
    {
        term: 'Choropleth Map',
        definition: 'A thematic map in which areas are shaded or patterned in proportion to a statistical variable, such as the Geopolitical Risk Score of a country.',
        category: 'Technical'
    },
    {
        term: 'Resiliency',
        definition: 'The capacity of an organization to absorb, adapt, and recover from a disruptive event while maintaining essential functions.',
        category: 'Strategic'
    },
    {
        term: 'Mitigation',
        definition: 'Actions taken to reduce the probability or impact of a risk event, such as installing physical barriers or implementing travel safety protocols.',
        category: 'Security'
    },
    {
        term: 'Ground Truth',
        definition: 'Information provided by direct observation (on-the-ground intelligence) as opposed to information provided by remote sensing or inference.',
        category: 'Strategic'
    },
    {
        term: 'PMESII Variable',
        definition: 'One of the six initial elements (Political, Military, Economic, Social, Infrastructure, Information) used to build a baseline profile of a nation-state.',
        category: 'Geopolitical'
    },
    {
        term: 'CARVER Matrix',
        acronym: 'CARVER',
        definition: 'A methodology used to assess target vulnerability across six factors: Criticality, Accessibility, Recuperability, Vulnerability, Effect, and Recognizability.',
        category: 'Security',
        externalUrl: 'https://en.wikipedia.org/wiki/CARVER_matrix'
    },
    {
        term: 'Duty of Care',
        definition: 'A legal and ethical obligation of an organization to ensure the safety and well-being of its employees, especially while traveling for business.',
        category: 'Strategic'
    },
    {
        term: 'Single Point of Failure',
        definition: 'A component of a system that, if it fails, will stop the entire system from working. Identified via Business Impact Analysis (BIA).',
        category: 'Strategic'
    },
    {
        term: 'Asset',
        definition: 'Any person, place, or thing of value to an organization that must be protected, such as a CEO, a regional office, or internal intellectual property.',
        category: 'Security'
    },
    {
        term: 'Vulnerability',
        definition: 'A weakness or gap in a security program, physical structure, or digital system that can be exploited by a threat actor to gain unauthorized access.',
        category: 'Security'
    },
    {
        term: 'Threat Vector',
        definition: 'The specific method or path used by a threat actor to access an asset, such as a phishing email, a physical perimeter breach, or a supply chain disruption.',
        category: 'Security'
    },
    {
        term: 'GDELT',
        acronym: 'GDELT',
        definition: 'The Global Database of Events, Language, and Tone. A primary data source for our map, monitoring the world\'s broadcast, print, and web news in over 100 languages.',
        category: 'Technical'
    },
    {
        term: 'Telemetry',
        definition: 'The automated process of collecting and transmitting data from remote or inaccessible sources (e.g., satellite imagery, digital sensor feeds, news aggregators).',
        category: 'Technical'
    },
    {
        term: 'Sentiment Analysis',
        definition: 'An AI-driven technique used to determine whether a piece of writing (e.g., a news report or social media post) is positive, negative, or neutral.',
        category: 'Technical'
    },
    {
        term: 'Noise Floor',
        definition: 'A term used to describe the baseline level of irrelevant or "junk" data in an intelligence feed that analysts must filter out to find useful "signals."',
        category: 'Strategic'
    },
    {
        term: 'Confidence Score',
        definition: 'A statistical percentage representing how reliable we believe a piece of intelligence is, based on the quantity and quality of corroborating sources.',
        category: 'Strategic'
    },
    {
        term: 'ISO 31000',
        definition: 'The international standard for risk management, providing principles and generic guidelines on managing risks faced by organizations.',
        category: 'Strategic'
    },
    {
        term: 'CRI',
        acronym: 'CRI',
        definition: 'Composite Risk Index. Our proprietary 0-100 score that combines geopolitical, security, and economic variables into a single unified risk metric.',
        category: 'Strategic'
    },
    {
        term: 'Tactical Briefing',
        definition: 'A highly focused, concise report intended for immediate action or situational awareness, often regarding a specific event or location.',
        category: 'Strategic'
    },
    {
        term: 'Geopolitical Risk',
        definition: 'The risk that an investment\'s returns could suffer as a result of political changes or instability in a country.',
        category: 'Geopolitical'
    },
    {
        term: 'Tactical Intelligence',
        definition: 'Time-sensitive intelligence focused on immediate threats, operational conditions, and decisions affecting a specific location, asset, or mission.',
        category: 'Strategic'
    },
    {
        term: 'Global Risk Monitor',
        definition: 'A consolidated monitoring view that tracks geopolitical, security, humanitarian, and operational risk signals across countries and regions.',
        category: 'Geopolitical'
    },
    {
        term: 'Risk Index Matrix',
        definition: 'A comparative view that organizes normalized risk scores by geography, severity, or category to support prioritization and trend analysis.',
        category: 'Strategic'
    },
    {
        term: 'Critical',
        definition: 'The highest risk severity classification, indicating immediate material exposure requiring urgent review and response.',
        category: 'Security'
    },
    {
        term: 'Country Analysis & Strategic Reporting',
        definition: 'Country-level intelligence combining geopolitical conditions, security threats, governance, economics, and operational implications into decision-ready reporting.',
        category: 'Geopolitical'
    },
    {
        term: 'Tactical Indicators',
        definition: 'Observable near-term signals that may reveal emerging threats, changing operating conditions, or increased risk to people and assets.',
        category: 'Security'
    },
    {
        term: 'Criticality',
        definition: 'A measure of how vital an asset or process is to the continued operation of an organization. Loss of a critical asset causes immediate system failure.',
        category: 'Security'
    },
    {
        term: 'ACAPS',
        acronym: 'ACAPS',
        definition: 'Assessment Capacities Project. An independent information provider that provides free, independent humanitarian analysis.',
        category: 'Geopolitical'
    },
    {
        term: 'SME',
        acronym: 'SME',
        definition: 'Subject Matter Expert. An individual with deep knowledge and experience in a specific field, such as regional logistics or cyber defense.',
        category: 'Strategic'
    },
    {
        term: 'Non-Governmental Organization',
        acronym: 'NGO',
        definition: 'A non-profit, citizen-based group that functions independently of government, often active in conflict zones or disaster areas.',
        category: 'Geopolitical'
    },
    {
        term: 'Normalization',
        definition: 'The technical process of adjusting values measured on different scales to a common scale, allowed us to compare different countries accurately.',
        category: 'Technical'
    },
    {
        term: 'Pattern Recognition',
        definition: 'The ability of our AI to identify recurring "signatures" in geopolitical data that often precede major events like coups or riots.',
        category: 'Technical'
    },
    {
        term: 'Signal versus Noise',
        definition: 'The challenge of distinguishing useful, actionable intelligence (the signal) from irrelevant or distracting background information (the noise).',
        category: 'Strategic'
    },
    {
        term: 'Duty of Care',
        definition: 'The legal and moral obligation of an organization to ensure the safety of its staff, especially in high-risk environments.',
        category: 'Strategic'
    },
    {
        term: 'Business Impact Analysis',
        acronym: 'BIA',
        definition: 'A systematic process that predicts the consequences of disruption of a business function and gathers information needed to develop recovery strategies.',
        category: 'Strategic'
    },
    {
        term: 'Incident Response',
        definition: 'The predefined methodology an organization uses to respond to and manage a security breach or a geopolitical crisis.',
        category: 'Security'
    },
    {
        term: 'Heatmap',
        definition: 'A visualization technique that uses colors to show where there is a high density of events or threats in a specific geographic area.',
        category: 'Technical'
    },
    {
        term: 'Risk Mitigation',
        definition: 'The process of implementing measures to reduce either the likelihood of a threat occurring or the impact if it does occur.',
        category: 'Strategic'
    },
    {
        term: 'Vetting',
        definition: 'The process of performing a background check on a person, vendor, or organization before engaging in a business relationship.',
        category: 'Security'
    },
    {
        term: 'Soft Target',
        definition: 'A person or thing that is relatively unprotected or vulnerable to attack, in contrast to a "hard target" like a fortified military base.',
        category: 'Security'
    },
    {
        term: 'KRI',
        acronym: 'KRI',
        definition: 'Key Risk Indicator. A metric used by organizations to provide an early signal of increasing risk exposure in various areas of the business.',
        category: 'Strategic'
    }
];
